<template>
  <v-skeleton-loader
    :loading="isLoading"
    transition="expand-transition"
    type="article"
  >
  <v-container class="text-center">
    <v-form :disabled="!isActive">
    <v-row>
      <v-col cols="12" sm="6" md="6" lg="3" xl="3">
        <AdminTextField
          data-cy="FormDeliveryNote-receivingDate"
          :label="$$t('receivingDate')"
          v-model="receivingDate" @input="setReceivingDate"
          v-on:keyup.enter="goNext($event.target)"
        />
      </v-col>
      <v-col cols="12" sm="6" md="6" lg="3" xl="3">
        <v-select
          data-cy="FormDeliveryNote-farmId"
          filled
          :label="$$t('farmId')"
          :items="farms"
          item-text="name"
          item-value="id"
          v-model="localDeliveryNote.farmId"
          v-on:keyup.enter="goNext($event.target)"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" md="6" lg="3" xl="3">
        <v-text-field
          filled
          data-cy="FormDeliveryNote-goodsReceivedNumber"
          :label="$$t('goodsReceivedNumber')"
          v-model="localDeliveryNote.goodsReceivedNumber"
          v-on:keyup.enter="goNext($event.target)"
        />
      </v-col>
      <v-col cols="12" sm="6" md="6" lg="3" xl="3">
        <v-text-field
          filled
          data-cy="FormDeliveryNote-farmDeliveryNoteNumber"
          :label="$$t('farmDeliveryNoteNumber')"
          v-model="localDeliveryNote.farmDeliveryNoteNumber"
          v-on:keyup.enter="completedHeader"
        />
      </v-col>
    </v-row>
    </v-form>
    <v-row>
      <v-col cols="12">
        <v-btn
          data-cy="EditDeliveryNote-addLine"
          v-if="isActive"
          color="success"
          :loading="isSaving"
          @click="addLine">Add Line</v-btn>
        <table-stock-items
          v-if="stockItems.length > 0"
          ref="stockItems"
          v-model="stockItems"
          @remove="remove"
          :isActive="isActive"
        />
      </v-col>
    </v-row>
    <v-row>
      <div class="d-flex justify-end mb-6">
        <v-btn color="default" outlined class="ml-4 btn-cancel"
          v-if="mode === 'add'"
          :to="{ name: from.name }"
          data-cy="EditDeliveryNote-button-cancel">
          {{ $t('common.buttons.cancel') }}
        </v-btn>
        <v-btn color="default" outlined class="ml-4 btn-cancel"
          v-if="mode === 'edit' && !isActive"
          :to="{ name: from.name }"
          data-cy="EditDeliveryNote-button-back">
          {{ $t('common.buttons.back') }}
        </v-btn>
        <v-btn color="success" type="button" class="ml-4" :loading="isSaving" @click="save"
          v-if="mode === 'edit' && isActive"
          data-cy="EditDeliveryNote-button-save">
            {{ $t('common.buttons.save') }}
        </v-btn>
        <v-btn color="primary" type="button" class="ml-4" :loading="isSaving" @click="complete"
          v-if="mode === 'edit' && isActive"
          data-cy="EditDeliveryNote-button-complete">
            {{ $t('common.buttons.complete') }}
        </v-btn>
      </div>
    </v-row>
  </v-container>
  </v-skeleton-loader>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import loading from '@codehq/aurora-app-core/src/mixins/loading';
import dayjs from 'dayjs';
import TableStockItems from '../components/TableStockItems.vue';
import form from '../mixins/form';
import AdminTextField from '../components/AdminTextField.vue';

export default {
  components: {
    TableStockItems,
    AdminTextField,
  },
  name: 'EditDeliveryNote',
  module: 'StockItem',
  mixins: [loading, form],
  data() {
    return {
      /**
      * Get/Set editing StockItem. Default to {}.
      */
      editStockItem: {},
      stockItems: [],
      /**
       * Query Id for StockItem
       */
      deliveryNoteId: this.$route?.query.id,
      localDeliveryNote: {
        state: 'Receiving',
        receivingDate: this.formatDateTime(new Date()),
      },
      receivingDate: this.formatDateTime(new Date()),
    };
  },
  computed: {
    isActive() {
      return this.localDeliveryNote.state === 'Receiving';
    },
    /** Vuex states
     */
    ...mapState('farms', ['farms']),
    /** Get the mode for this view, create or update
     */
    mode() {
      if (this.localDeliveryNote.id) {
        return 'edit';
      }
      return 'add';
    },
  },
  async mounted() {
    this.isLoading = true;
    await this.LOAD_farms();
    if (this.$route?.query.id) {
      this.localDeliveryNote = await this.LOAD_deliveryNote(this.$route?.query.id);
      this.stockItems = await this.LOAD_stockItemsByDeliveryNote(this.localDeliveryNote.id);
      this.receivingDate = this.formatDateTime(this.localDeliveryNote.receivingDate);
    }
    this.isLoading = false;
  },
  methods: {
    /** Vuex methods
     */
    ...mapActions('farms', ['LOAD_farms', 'DELETE_farm']),
    ...mapActions('deliveryNotes', ['CREATE_deliveryNote', 'UPDATE_deliveryNote', 'LOAD_deliveryNote']),
    ...mapActions('stockItems', ['CREATE_stockItem', 'UPDATE_stockItem', 'DELETE_stockItem', 'LOAD_stockItemsByDeliveryNote']),
    async addLine() {
      const { localDeliveryNote } = this;
      if (!localDeliveryNote.farmId) {
        await this.$confirm('You need to select a farm first');
        return;
      }
      if (this.mode === 'add') {
        // create delivery note
        this.localDeliveryNote = await this.CREATE_deliveryNote(localDeliveryNote);
      }
      const stockItem = await this.CREATE_stockItem({
        deliveryNoteId: this.localDeliveryNote.id,
      });
      this.stockItems.push(stockItem);
    },
    async remove(item) {
      await this.DELETE_stockItem(item.id);
      this.stockItems.splice(this.stockItems.indexOf(item), 1);
    },
    /** Method used to complete the item
     */
    async complete() {
      this.isSaving = true;
      const { localDeliveryNote } = this;
      const { id } = localDeliveryNote;
      await this.UPDATE_deliveryNote({
        id,
        state: 'Received',
      });
      this.isSaving = false;
      await this.save();
    },
    /** Method used to save the item
     */
    async save() {
      this.isSaving = true;
      const { localDeliveryNote } = this;
      const {
        id,
        farmId,
        farmDeliveryNoteNumber,
        goodsReceivedNumber,
        receivingDate,
      } = localDeliveryNote;
      await this.UPDATE_deliveryNote({
        id,
        farmId,
        farmDeliveryNoteNumber,
        goodsReceivedNumber,
        receivingDate,
      });
      this.stockItems.forEach(async (stockItem) => {
        await this.UPDATE_stockItem(stockItem);
      });
      this.isSaving = false;
      this.$router.push({ name: 'processing-ListDeliveryNote' });
    },
    /**
     * Executes setReceivingDate
     */
    setReceivingDate(val) {
      this.localDeliveryNote.receivingDate = dayjs(val).format();
    },
  },
};
</script>
